import { Fragment, useCallback, useRef, useState, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import BookedSpeakerCard from "../BookedSpeakerCard/BookedSpeakerCard";
import { ExclamationCircleIcon, XMarkIcon, FunnelIcon } from "@heroicons/react/24/outline";

export default function Filters({ bookedData, open, setOpen, users = [], onUserFilterChange }) {
  const cancelButtonRef = useRef(null);
  const [resultFounded, setResultFounded] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    speakerName: "",
    meetingHouse: "",
    number: "",
    userId: ""
  });

  // Extract unique values from bookedData for dropdown options
  const filterOptions = useMemo(() => {
    if (!bookedData || bookedData.length === 0) return { meetingHouses: [], speakers: [] };
    
    const meetingHouses = [...new Set(bookedData.map(item => item.meetingHouse))].filter(Boolean).sort();
    const speakers = [...new Set(bookedData.map(item => item.speakerName))].filter(Boolean).sort();
    
    return { meetingHouses, speakers };
  }, [bookedData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setResultFounded([]); // Clear results when filters change
    
    // If it's a user filter change and callback is provided, call it
    if (field === 'userId' && onUserFilterChange) {
      onUserFilterChange(value || null);
    }
  };

  const clearAllFilters = () => {
    setFilters({
      speakerName: "",
      meetingHouse: "",
      number: "",
      userId: ""
    });
    setResultFounded([]);
    setIsLoading(false);
    
    // Also clear user filter if callback is provided
    if (onUserFilterChange) {
      onUserFilterChange(null);
    }
  };

  const search = useCallback(
    async (event) => {
      event.preventDefault();
      setIsLoading(true);
      
      // Simulate loading with 300ms timeout
      setTimeout(() => {
        if (!bookedData || bookedData.length === 0) {
          setResultFounded(null);
          setIsLoading(false);
          return;
        }

        const result = bookedData.filter((item) => {
          // Speaker name filter
          if (filters.speakerName && !item.speakerName?.toLowerCase().includes(filters.speakerName.toLowerCase())) {
            return false;
          }
          
          // Meeting house filter
          if (filters.meetingHouse && item.meetingHouse !== filters.meetingHouse) {
            return false;
          }
          
          // Number filter
          if (filters.number && item.number !== filters.number) {
            return false;
          }
          
          return true;
        });
        
        setResultFounded(!result.length ? null : result);
        setIsLoading(false);
      }, 200);
    },
    [bookedData, filters],
  );

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(value => value !== "");


  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-lg">
                <div className="bg-white px-4 pb-4 pt-3 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <FunnelIcon className="w-5 h-5 text-gray-600" />
                      <div className="font-semibold">Φίλτρα</div>
                    </div>
                    {hasActiveFilters && (
                      <button
                        type="button"
                        onClick={clearAllFilters}
                        className="text-sm text-gray-500 hover:text-gray-700 underline"
                      >
                        Καθαρισμός όλων
                      </button>
                    )}
                  </div>
                  
                  <form onSubmit={search} className="space-y-4">
                    {/* User Filter */}
                    {users && users.length > 0 && (
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Χρήστης
                        </label>
                        <select
                          value={filters.userId}
                          onChange={(e) => handleFilterChange('userId', e.target.value)}
                          className="shadow-sm rounded-md w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="">Όλοι οι χρήστες</option>
                          {users.map((user) => (
                            <option key={user.id} value={user.uid}>
                              {user.name || user.email}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Speaker Name Filter */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-900">
                        Ομιλητής
                      </label>
                      <select
                        value={filters.speakerName}
                        onChange={(e) => handleFilterChange('speakerName', e.target.value)}
                        className="shadow-sm rounded-md w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Όλοι οι ομιλητές</option>
                        {filterOptions.speakers.map((speaker) => (
                          <option key={speaker} value={speaker}>
                            {speaker}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Meeting House Filter */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-900">
                        Αίθουσα συνάθροισης
                      </label>
                      <select
                        value={filters.meetingHouse}
                        onChange={(e) => handleFilterChange('meetingHouse', e.target.value)}
                        className="shadow-sm rounded-md w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Όλες οι αίθουσες</option>
                        {filterOptions.meetingHouses.map((house) => (
                          <option key={house} value={house}>
                            {house}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Talk Number Filter */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-900">
                        Αρ. ομιλίας
                      </label>
                      <input
                        type="text"
                        value={filters.number}
                        onChange={(e) => handleFilterChange('number', e.target.value)}
                        placeholder="π.χ. 84"
                        className="shadow-sm rounded-md w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`flex-1 inline-flex justify-center items-center gap-2 rounded-md px-3 py-2 focus:outline-none text-sm font-semibold shadow-sm ${
                          isLoading 
                            ? 'bg-indigo-400 text-white cursor-not-allowed' 
                            : 'bg-indigo-600 text-white hover:bg-indigo-500'
                        }`}
                        ref={cancelButtonRef}
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Αναζήτηση...
                          </>
                        ) : (
                          'Αναζήτηση'
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="inline-flex bg-gray-300 justify-center rounded-md text-gray-700 px-4 py-2 focus:outline-none text-sm font-semibold shadow-sm hover:bg-gray-200"
                      >
                        Κλείσιμο
                      </button>
                    </div>
                  </form>

                  {/* Results Section */}
                  {resultFounded === null && (
                    <div className="text-center py-6 mt-6 border-t border-gray-200">
                      <ExclamationCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 mb-1">Δεν βρέθηκαν αποτελέσματα</p>
                      <p className="text-xs text-gray-400">Δοκιμάστε να αλλάξετε τα φίλτρα αναζήτησης</p>
                    </div>
                  )}

                  {resultFounded && resultFounded.length > 0 && (
                    <div className="mt-6 border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-gray-900">
                          Αποτελέσματα ({resultFounded.length})
                        </h3>
                        <button
                          type="button"
                          onClick={() => setResultFounded([])}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          <XMarkIcon className="w-4 h-4 inline mr-1" />
                          Κλείσιμο
                        </button>
                      </div>
                      <div className="max-h-96 overflow-y-auto space-y-3">
                        {resultFounded.map((item, index) => (
                          <div
                            key={item.id || index}
                            className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-gray-300 transition-colors"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="text-sm font-medium text-gray-900">
                                {new Date(item?.bookedDate).toLocaleDateString('el-GR', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </div>
                              <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
                                #{item?.number}
                              </span>
                            </div>
                            <BookedSpeakerCard bookedData={item} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
